import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { createStaffMember, updateStaffMember, uploadStaffPhoto, StaffMember } from "@/services/staffService";
import { useAuth } from "@/hooks/useAuth";
import { Form } from "@/components/ui/form";
import BasicInfoFields from "./form/BasicInfoFields";
import RoleField from "./form/RoleField";
import PhotoUpload from "./form/PhotoUpload";
import StatusField from "./form/StatusField";
import LocationField from "./form/LocationField";
import StartDateField from "./form/StartDateField";
import FormActions from "./form/FormActions";
import { toast } from "sonner";
import { formatPhoneNumber } from "@/lib/format";

// Define a more complete staff form schema
const staffFormSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  email: z.union([
    z.string().email("Invalid email address"),
    z.string().length(0).transform(() => null),
    z.null()
  ]).optional(),
  phone: z.string().nullable().optional(),
  role: z.enum([
    "instructor",
    "admin",
    "billing",
    "front_desk"
  ]),
  is_active: z.boolean().default(true),
  profile_photo_url: z.string().nullable().optional(),
  location_id: z.string().nullable().optional(),
  start_date: z.string().nullable().optional(),
  birthday_month: z.number().min(1).max(12).nullable().optional(),
  birthday_day: z.number().min(1).max(31).nullable().optional(),
});

export type StaffFormValues = z.infer<typeof staffFormSchema>;

interface StaffFormProps {
  initialData?: StaffMember | null;
  onClose: (refresh?: boolean) => void;
  onSubmit?: (data: StaffFormValues, continueToDetails?: boolean) => Promise<void>;
}

const StaffForm: React.FC<StaffFormProps> = ({ initialData, onClose, onSubmit: onSubmitProp }) => {
  const { organizationId } = useAuth();
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(
    initialData?.profile_photo_url || null
  );
  const [isUploading, setIsUploading] = useState(false);

  const form = useForm<StaffFormValues>({
    resolver: zodResolver(staffFormSchema),
    defaultValues: {
      first_name: initialData?.first_name || "",
      last_name: initialData?.last_name || "",
      email: initialData?.email || "",
      phone: initialData?.phone || "",
      role: (initialData?.role as "instructor" | "admin" | "receptionist" | "manager" | "owner" | "front_desk" | "other") || "instructor",
      is_active: initialData?.is_active !== undefined ? initialData.is_active : true,
      profile_photo_url: initialData?.profile_photo_url || null,
      location_id: initialData?.location_id || null,
      start_date: initialData?.start_date || null,
      birthday: initialData?.birthday || null,
      birthday_month: null,
      birthday_day: null,
    },
  });

  const getInitials = (firstName: string, lastName: string) => {
    if (!firstName && !lastName) return "?";
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setPhotoFile(file);
      
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setPhotoPreview(event.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDefaultSubmit = async (values: StaffFormValues, continueToDetails: boolean = false) => {
    try {
      setIsUploading(true);
      
      console.log("🔍 Current Organization ID:", organizationId);

      // If custom onSubmit prop is provided, use it (for workflow handling)
      if (onSubmitProp) {
        await onSubmitProp(values, continueToDetails);
        return;
      }

      // Ensure phone is properly formatted
      const formattedPhone = values.phone ? formatPhoneNumber(values.phone) : null;
      
      let staffMember: StaffMember | null = null;

      // If initialData exists, we are editing an existing staff member
      if (initialData && initialData.id) {
        // Create a safe update payload with required fields
        const updateData: Record<string, any> = {
          // Always include these fields even if unchanged to ensure valid payload
          first_name: values.first_name,
          last_name: values.last_name
        };
        
        // Add other fields that have changed
        if (values.email !== initialData.email) {
          updateData.email = values.email || null;
        }
        
        if (formattedPhone !== initialData.phone) {
          updateData.phone = formattedPhone;
        }
        
        if (values.is_active !== initialData.is_active) {
          updateData.is_active = values.is_active;
        }
        
        // Be cautious with role updates - this might be restricted based on user permissions
        if (values.role !== initialData.role) {
          updateData.role = values.role;
        }
        
        // Include start_date if it has changed
        if (values.start_date !== initialData.start_date) {
          updateData.start_date = values.start_date;
        }

        console.log("📤 Staff Update Payload:", updateData);
        
        // Update existing staff
        staffMember = await updateStaffMember(initialData.id, updateData);
        
        if (staffMember) {
          try {
            // Import the necessary functions for location management
            const { getStaffLocations, assignStaffToLocation, removeStaffFromLocation } = 
              await import('@/services/staffService');
            
            // Get current location assignments
            const existingLocations = await getStaffLocations(initialData.id);
            
            // Handle location changes
            if (values.location_id === "none" || values.location_id === null) {
              // If "None" is selected, remove all location assignments
              console.log("Removing all location assignments");
              
              // Remove each existing location assignment
              for (const location of existingLocations) {
                await removeStaffFromLocation(initialData.id, location.location_id);
              }
            } else if (values.location_id !== initialData.location_id) {
              // A specific location is selected and it's different from the current one
              
              // See if staff is already assigned to this location
              const locationExists = existingLocations.some(
                loc => loc.location_id === values.location_id
              );
              
              if (!locationExists) {
                // Assign to the new location
                console.log(`Assigning staff to location: ${values.location_id}`);
                await assignStaffToLocation(
                  initialData.id,
                  values.location_id,
                  organizationId || "75b36a94-27a0-4de5-b5f5-fb3af35f0cf7"
                );
              } else {
                console.log("Staff already assigned to this location, skipping assignment");
              }
            }
          } catch (error) {
            console.error("Error handling staff location:", error);
            // Don't fail the whole operation if location management fails
          }
        }
      } else {
        // Create data for new staff
        const createData = {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email || null,
          phone: formattedPhone,
          role: values.role,
          is_active: values.is_active ?? true,
          profile_photo_url: null,
          organization_id: organizationId || "75b36a94-27a0-4de5-b5f5-fb3af35f0cf7",
          start_date: values.start_date,
          birthday: values.birthday,
          birthday_month: values.birthday_month,
          birthday_day: values.birthday_day,
        };

        console.log("📤 Staff Create Payload:", createData);
        
        // Create new staff
        staffMember = await createStaffMember(createData);
        
        // Assign the staff to a location if one is selected
        if (staffMember && values.location_id && values.location_id !== "none") {
          try {
            const { assignStaffToLocation } = await import('@/services/staffService');
            await assignStaffToLocation(
              staffMember.id,
              values.location_id,
              organizationId || "75b36a94-27a0-4de5-b5f5-fb3af35f0cf7"
            );
          } catch (error) {
            console.error("Error assigning staff to location:", error);
            // Don't fail the whole operation if location assignment fails
          }
        }
      }
      
      // Handle photo upload if a photo was selected
      if (staffMember && photoFile) {
        try {
          const photoUrl = await uploadStaffPhoto(photoFile, staffMember.id);
          
          if (photoUrl) {
            // Update the staff record with the photo URL
            await updateStaffMember(staffMember.id, {
              profile_photo_url: photoUrl
            });
          }
        } catch (error) {
          console.error("Error uploading staff photo:", error);
          // Don't fail the whole operation if photo upload fails
        }
      }
      
      if (staffMember) {
        const successMessage = initialData 
          ? "Staff member updated successfully" 
          : "Staff member created successfully";
        toast.success(successMessage);
        
        onClose(true);
      }
    } catch (error) {
      console.error("Error saving staff member:", error);
      const errorMessage = initialData 
        ? "Failed to update staff member" 
        : "Failed to create staff member";
      toast.error(errorMessage, {
        description: error instanceof Error ? error.message : "Unknown error"
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Regular submit handler (no details)
  const handleFormSubmit = (values: StaffFormValues) => {
    handleDefaultSubmit(values, false);
  };

  // Submit and continue to details handler
  const handleFormSubmitAndContinue = (values: StaffFormValues) => {
    handleDefaultSubmit(values, true);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
        <PhotoUpload 
          photoPreview={photoPreview}
          getInitials={getInitials}
          handleFileChange={handleFileChange}
          firstName={form.watch('first_name')}
          lastName={form.watch('last_name')}
          hasExistingPhoto={!!initialData?.profile_photo_url}
        />

        <BasicInfoFields form={form} />
        <div className="grid grid-cols-2 gap-4">
          <StartDateField form={form} />
          <LocationField form={form} />
        </div>
        <RoleField form={form} />
        <StatusField form={form} />
        
        <FormActions 
          isUploading={isUploading}
          onCancel={() => onClose()}
          isEditing={!!initialData}
          onSubmitAndContinue={() => form.handleSubmit(handleFormSubmitAndContinue)()}
        />
      </form>
    </Form>
  );
};

export default StaffForm;
